import {Box, Grid, makeStyles, Typography} from '@material-ui/core'
import moment from 'moment'
import React, {ChangeEvent, useEffect, useState} from 'react'
import {Order, OrderPallet, OrderUnit} from 'src/api/models'
import {skuit} from 'src/services/skuit'
import {Barcode} from 'src/components/Barcode'
import {useQueryPaginator} from 'src/components/Table'
import useProfileContext from 'src/context/ProfileContext'


const useStyles = makeStyles((theme) => ({
  card: {
    border: '1px solid gray',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    margin: theme.spacing(2),
    maxWidth: '96%',
  },
  section: {
    minHeight: '100%',
    paddingTop: theme.spacing(2),
  },
  units: {
    fontSize: theme.spacing(4),
    fontWeight: 'bold',
  },
  break: {
    wordBreak: 'break-word',
  },
}))

interface PickTicketProps {
  order: Order
  onReadyToPrint?: () => void
}

interface UnitCardProps {
  orderUnit: OrderUnit
}

const UnitCard = (props: UnitCardProps) => {
  const {orderUnit} = props
  const classes = useStyles()
  const barcode = {barcode: orderUnit.sku?.guid || '', type: 'datamatrix', displayName: ''}

  return (
    <div className="card-break ">
      <Grid container spacing={3} component="article" className={classes.card}>
        <Grid item xs={12}>
          <Grid container spacing={4} alignItems="stretch" alignContent="stretch">
            <Grid item xs={2}>
              <Barcode barcode={barcode} />
            </Grid>
            <Grid item xs={5}>
              <Grid
                container
                direction="column"
                alignItems="flex-start"
                justify="flex-end"
                spacing={2}
                className={classes.section}
              >
                <Grid item>
                  <Typography variant="body1" className={classes.break}>
                    {orderUnit.sku?.guid}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1">
                    [{orderUnit.sku?.product?.primaryBarcode?.type}]{' '}
                    {orderUnit.product?.primaryBarcode?.barcode}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={5}>
              <Grid
                container
                direction="column"
                alignItems="flex-start"
                justify="flex-start"
                spacing={2}
                className={classes.section}
              >
                <Grid item>
                  <Typography variant="body1">
                    [{orderUnit?.sku?.sku}] {orderUnit?.sku?.product?.name}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" className={classes.units}>
                    X {orderUnit?.numUnits}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

interface PalletCardProps {
  orderPallet: OrderPallet
}

const PalletCard = (props: PalletCardProps) => {
  const {orderPallet} = props
  const classes = useStyles()
  const barcode = {barcode: orderPallet.pallet?.guid || '', type: 'datamatrix', displayName: ''}

  return (
    <div className="card-break">
      <Grid container spacing={3} component="article" className={classes.card}>
        <Grid item xs={12}>
          <Grid container spacing={4} alignItems="stretch" alignContent="stretch">
            <Grid item xs={2}>
              <Barcode barcode={barcode} />
            </Grid>
            <Grid item xs={10} className={classes.section}>
              <Grid
                container
                direction="column"
                alignItems="flex-start"
                justify="flex-end"
                spacing={2}
              >
                <Grid item>
                  <Typography variant="body1">{orderPallet.pallet?.name}</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1">{orderPallet.pallet?.clientID ?? '--'}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

export const PickTicket = (props: PickTicketProps) => {
  const {order, onReadyToPrint} = props
  const [readyToPrint, setReadyToPrint] = useState(false)
  const [totalUnits, setTotalUnits] = useState<Array<OrderUnit>>([])
  const [totalPallets, setTotalPallets] = useState<Array<OrderPallet>>([])

  const [allUnitsReady, setAllUnitsReady] = useState(false)
  const [allPalletsReady, setAllPalletsReady] = useState(false)

  const profileContext = useProfileContext()

  const unitsPaginator = useQueryPaginator('order-units-list', skuit.sc.freight.orders.units.list, {
    params: {page_size: 100, orders: order.guid},
  })
  const palletsPaginator = useQueryPaginator('order-pallets', skuit.sc.freight.orders.pallets.list, {
    params: {page_size: 100, orders: order.guid},
  })

  const {
    loading: loadingUnits,
    data: unitsData,
    page: unitsPage,
    handlePageChange: handleUnitsPageChange,
  } = unitsPaginator
  const {
    loading: loadingPallets,
    data: palletsData,
    page: palletsPage,
    handlePageChange: handlePalletsPageChange,
  } = palletsPaginator

  useEffect(() => {
    if (!loadingUnits && unitsData && !allUnitsReady) {
      const {next, results} = unitsData
      setTotalUnits([...totalUnits, ...results])
      if (next !== null) {
        const event = {
          target: {
            value: 'z',
          },
        } as ChangeEvent<HTMLInputElement>
        handleUnitsPageChange(event, unitsPage + 1)
      } else {
        setAllUnitsReady(true)
      }
    }
  }, [unitsData, allUnitsReady, handleUnitsPageChange, loadingUnits, totalUnits, unitsPage])

  useEffect(() => {
    if (!loadingPallets && palletsData && !allPalletsReady) {
      const {results, next} = palletsData
      setTotalPallets([...totalPallets, ...results])
      if (next !== null) {
        const event = {
          target: {
            value: 'z',
          },
        } as ChangeEvent<HTMLInputElement>
        handlePalletsPageChange(event, palletsPage + 1)
      } else {
        setAllPalletsReady(true)
      }
    }
  }, [
    palletsData,
    allPalletsReady,
    handlePalletsPageChange,
    loadingPallets,
    palletsPage,
    totalPallets,
  ])

  useEffect(() => {
    if (allUnitsReady && allPalletsReady) {
      setReadyToPrint(true)
    }
  }, [allUnitsReady, allPalletsReady, setReadyToPrint])

  useEffect(() => {
    if (readyToPrint) {
      onReadyToPrint?.()
    }
  }, [readyToPrint, onReadyToPrint])

  useEffect(() => {
    setTotalPallets([])
    setTotalUnits([])
    setAllPalletsReady(false)
    setAllUnitsReady(false)
  }, [order.guid])

  if (!readyToPrint) {
    return <div>Pick Ticket not ready to print</div>
  }

  return (
    <div className="always-page-break">
      <Box px={20}>
        <Box textAlign="center">
          <Typography variant="caption" align="center">
            Pick Ticket for {order.guid}, printed on {moment().format('lll')} by {profileContext.profile?.firstName} {profileContext.profile?.lastName}
          </Typography>
        </Box>
        <Box>
          <Typography variant="h3" gutterBottom align="center">
            Order #{order?.number}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle1" align="center">
            Fullfilled on {moment().format('ll')}
          </Typography>
        </Box>
      </Box>
      <div className="item-container">
        {allUnitsReady && totalUnits.length > 0 && (
          <Box marginTop={6}>
            <Typography variant="h4" align="center" gutterBottom>
              Product (
              {totalUnits?.reduce(
                (sum, shipmentUnit) => sum + (shipmentUnit.numUnits ? +shipmentUnit.numUnits : 0),
                0
              )}{' '}
              total units)
            </Typography>
            {totalUnits.map((unit) => (
              <UnitCard
                orderUnit={unit}
                key={unit?.guid}
              />
            ))}
          </Box>
        )}
      </div>
      <div>
        {allPalletsReady && totalPallets.length > 0 && (
          <Box marginTop={6}>
            <Typography variant="h4" align="center" gutterBottom>
              {`Pallets (${palletsData.count})`}
            </Typography>
            {totalPallets.map((row) => (
              <PalletCard key={row.guid} orderPallet={row} />
            ))}
          </Box>
        )}
      </div>
    </div>
  )
}
